import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { LoadingSpinner } from "@/components/loading-spinner"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchInput, SuggestionSearch } from "@/components/ui/search"
import { useClickOutside } from "@/hooks/useClickOutside"
import { cn } from "@/lib/utils"
import { IcdCodeSeparator } from "@/modules/patients/detail/episode-of-care/icd-code-separator"
import { useFetchIcdCodesQuery } from "@/modules/patients/hooks/useIcdDiagnosisQuery"
import { IcdCodeSearchOption } from "@/modules/patients/libs/icd-code-search-option"
import { IcdDiagnosisFragment$data } from "@/modules/patients/services/__generated__/IcdDiagnosisFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { IcdCodesSearchType } from "@/types/__generated__/union-types"

interface IcdCodeSearchProps {
  addedIcdCodes: string[]
  onIcdCodeSelected: (diagnosisCode: string, text: string) => void
  searchInputPlaceHolder: string
  searchType: IcdCodesSearchType
  searchName: string
  autoFocus?: boolean
}

export const IcdCodeSearch = (props: IcdCodeSearchProps) => {
  const { onIcdCodeSelected, searchInputPlaceHolder, addedIcdCodes, searchName, searchType, autoFocus = false } = props
  const { t } = useTranslation()
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()

  const [searchValue, setSearchValue] = useState("")
  const [showSearchResults, setShowSearchResults] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)

  const { icdCodes, debounceSearch, isLoading } = useFetchIcdCodesQuery({
    birthDay: patient.birthDate,
    gender: patient.gender,
    searchType,
  })

  const handleClearSearch = () => {
    setSearchValue("")
    debounceSearch("")
    setShowSearchResults(false)
    searchRef.current?.focus()
  }

  const handleChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    if (value !== searchValue) {
      setSearchValue(value)
      debounceSearch(value)
      setShowSearchResults(true)
    }
  }

  const handleSelectOption = (selectedOption: IcdDiagnosisFragment$data) => {
    if (!icdCodes) return

    const option =
      icdCodes.commons.find((o) => o.entry.diagnosisCode === selectedOption.diagnosisCode) ??
      icdCodes.others.find((o) => o.entry.diagnosisCode === selectedOption.diagnosisCode)

    if (option) {
      onIcdCodeSelected(option.entry.diagnosisCode, option.entry.text)
      setSearchValue("")
      debounceSearch("")
      setShowSearchResults(false)
    }
  }

  useClickOutside((event: MouseEvent) => {
    if (searchResultRef.current && !searchResultRef.current.contains(event.target as HTMLElement)) {
      setShowSearchResults(false)
    }
  }, searchRef)

  const areAllCommonItemsAdded = icdCodes?.commons.every((item) => addedIcdCodes.includes(item.entry.diagnosisCode))
  const areAllOtherItemsAdded = icdCodes?.others.every((item) => addedIcdCodes.includes(item.entry.diagnosisCode))

  useEffect(() => {
    if (searchRef.current && autoFocus) {
      searchRef.current.focus()
    }
  }, [autoFocus])

  return (
    <div className="pr-2">
      <SuggestionSearch className="my-2">
        <SearchInput
          className="bg-surface-subdued h-10"
          data-testid={`health-${searchName}-search-input`}
          ref={searchRef}
          value={searchValue}
          placeholder={searchInputPlaceHolder}
          onClear={handleClearSearch}
          onChange={handleChange}
          autoFocus={false}
          onFocus={() => setShowSearchResults(true)}
        />
        <SearchContent
          ref={searchResultRef}
          className={cn(searchValue.length >= 3 && showSearchResults ? "show" : "hidden", "px-0")}
          data-testid={`${searchName}-search-result`}>
          {isLoading && (
            <div className="my-2 flex w-full items-center justify-center">
              <LoadingSpinner size={24} />
            </div>
          )}
          {!isLoading &&
            (!icdCodes || (icdCodes.commons.length === 0 && icdCodes.others.length === 0) ? (
              <div
                className="text-on-surface-subdued relative cursor-default px-2 text-sm select-none"
                data-testid="nothing-found-message">
                {t("common.nothingFound")}
              </div>
            ) : (
              <ScrollArea viewportClassName="max-h-75">
                <div className="p-2">
                  {!!icdCodes.commons.length && (
                    <>
                      {!areAllCommonItemsAdded && (
                        <div className="text-on-surface-subdued py-2.5 pl-2 text-sm leading-5 font-medium">
                          {t("episodeOfCare.common")}
                        </div>
                      )}
                      {icdCodes.commons.map((option) => {
                        return (
                          addedIcdCodes &&
                          !addedIcdCodes.includes(option.entry.diagnosisCode) && (
                            <IcdCodeSearchOption
                              key={option.entry.diagnosisCode}
                              option={option.entry}
                              highlights={option.highlights}
                              onSelect={handleSelectOption}
                            />
                          )
                        )
                      })}
                    </>
                  )}
                  {!!icdCodes.others.length && (
                    <>
                      {!areAllCommonItemsAdded && !!icdCodes.commons.length && <IcdCodeSeparator />}
                      {!areAllOtherItemsAdded && (
                        <div className="text-on-surface-subdued py-2.5 pl-2 text-sm leading-5 font-medium">
                          {t("episodeOfCare.other")}
                        </div>
                      )}
                      {icdCodes.others.map((option) => {
                        return (
                          addedIcdCodes &&
                          !addedIcdCodes.includes(option.entry.diagnosisCode) && (
                            <IcdCodeSearchOption
                              key={option.entry.diagnosisCode}
                              option={option.entry}
                              highlights={option.highlights}
                              onSelect={handleSelectOption}
                            />
                          )
                        )
                      })}
                    </>
                  )}
                </div>
              </ScrollArea>
            ))}
        </SearchContent>
      </SuggestionSearch>
    </div>
  )
}
